import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 font-bold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-zooprimary focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        primary:
          'bg-zooprimary text-zoowhite shadow-md hover:bg-zooprimaryhover active:scale-[0.98]',
        secondary: 'bg-zoovpalelavender text-zooprimary hover:bg-zoopalelavender',
        outline: 'border border-zooborder text-zooindigo bg-zoowhite hover:bg-zoovpalelavender',
        outlinePrimary:
          'border border-zooprimary text-zooprimary bg-zoowhite hover:bg-zoovpalelavender',
        ghost: 'text-zoomuted hover:bg-zoovpalelavender hover:text-zooindigo',
        destructive: 'bg-zooerrortext text-zoowhite shadow-md hover:bg-zoodangerhover',
        destructiveGhost: 'text-zoomuted hover:bg-zooerrorbg hover:text-zooerrortext',
        link: 'text-zooprimary hover:underline',
        linkDanger: 'text-zooerrortext hover:underline',
      },
      size: {
        default: 'px-6 py-2.5 text-sm rounded-xl',
        small: 'px-4 py-2 text-sm rounded-xl',
        large: 'px-8 py-4 text-base rounded-2xl',
        chip: 'px-3 py-1.5 text-xs rounded-lg',
        icon: 'w-10 h-10 rounded-lg',
      },
    },
    compoundVariants: [
      {
        variant: ['link', 'linkDanger'],
        class: 'px-0 py-0 h-auto min-h-6 w-auto shadow-none rounded-none hover:bg-transparent',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
);

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const spinnerSizeBySize: Record<string, string> = {
  default: 'w-4 h-4',
  small: 'w-4 h-4',
  large: 'w-5 h-5',
  chip: 'w-3.5 h-3.5',
  icon: 'w-5 h-5',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    asChild = false,
    isLoading = false,
    type = 'button',
    children,
    ...buttonProps
  },
  forwardedRef
) {
  if (asChild === true) {
    return (
      <Slot
        ref={forwardedRef}
        className={cn(buttonVariants({ variant, size }), className)}
        {...buttonProps}
      >
        {children}
      </Slot>
    );
  }
  const resolvedSize = size === undefined || size === null ? 'default' : size;
  const spinnerSizeClassName = spinnerSizeBySize[resolvedSize];
  const isButtonDisabled = buttonProps.disabled === true || isLoading === true;
  return (
    <button
      ref={forwardedRef}
      type={type}
      {...buttonProps}
      disabled={isButtonDisabled}
      aria-busy={isLoading === true ? true : undefined}
      className={cn(buttonVariants({ variant, size }), className)}
    >
      {isLoading === true ? <LoadingSpinner className={spinnerSizeClassName} /> : null}
      {children}
    </button>
  );
});
